import React, { useCallback, useEffect, useState } from "react";
import { Navigate, Link } from "react-router-dom";
import { CheckCircle2, EyeOff, Trash2, RefreshCw, ShieldAlert, Image as ImageIcon } from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Stars } from "./Reviews";

function AdminPhoto({ id }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let u;
    api.get(`/admin/reviews/${id}/photo`, { responseType: "blob" }).then((r) => { u = URL.createObjectURL(r.data); setUrl(u); }).catch((e) => console.error("Could not load review photo", e));
    return () => u && URL.revokeObjectURL(u);
  }, [id]);
  if (!url) return <div className="h-40 w-full rounded bg-[color:var(--muted)] flex items-center justify-center"><ImageIcon size={20} className="text-[color:var(--text-muted)]"/></div>;
  return (
    <a href={url} download={`review-${id}.jpg`} data-testid={`admin-review-photo-${id}`}>
      <img src={url} alt="customer product" className="w-full max-h-64 object-cover rounded"/>
    </a>
  );
}

const STATUS_STYLE = { pending: "bg-amber-100 text-amber-900 border-amber-200", approved: "bg-emerald-100 text-emerald-900 border-emerald-200", hidden: "bg-stone-100 text-stone-700 border-stone-200" };

function AdminReviewCard({ r, onStatus, onDelete }) {
  return (
    <div data-testid={`admin-review-${r.review_id}`} className="rounded-lg border hairline bg-white p-5 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div>
          <Stars value={r.rating} size={15}/>
          <div className="mt-1 text-sm font-medium" style={{ color: "var(--primary)" }}>{r.display_name} <span className="text-[color:var(--text-muted)] font-normal">· {r.email}</span></div>
          <div className="text-xs text-[color:var(--text-muted)]">{new Date(r.created_at).toLocaleString()}{r.product ? ` · ${r.product}` : ""}</div>
        </div>
        <Badge className={STATUS_STYLE[r.status]} data-testid={`admin-review-status-${r.review_id}`}>{r.status}</Badge>
      </div>
      {r.has_photo && <AdminPhoto id={r.review_id}/>}
      {r.title && <div className="font-serif text-xl" style={{ color: "var(--primary)" }}>{r.title}</div>}
      <p className="text-sm whitespace-pre-line leading-relaxed">{r.body}</p>
      {!r.permission && <div className="flex items-center gap-2 text-xs text-rose-700" data-testid={`admin-review-no-permission-${r.review_id}`}><ShieldAlert size={14}/>Customer did not give permission to post — keep private.</div>}
      <div className="flex flex-wrap gap-2 pt-1">
        {r.status !== "approved" && r.permission && (
          <Button size="sm" data-testid={`admin-review-approve-${r.review_id}`} onClick={() => onStatus(r.review_id, "approved")} className="rounded-full btn-primary"><CheckCircle2 size={14} className="mr-1"/>Approve & post</Button>
        )}
        {r.status === "approved" && (
          <Button size="sm" variant="outline" data-testid={`admin-review-hide-${r.review_id}`} onClick={() => onStatus(r.review_id, "hidden")} className="rounded-full"><EyeOff size={14} className="mr-1"/>Hide from site</Button>
        )}
        <Button size="sm" variant="ghost" data-testid={`admin-review-delete-${r.review_id}`} onClick={() => onDelete(r.review_id)} className="rounded-full text-rose-700 hover:bg-rose-50"><Trash2 size={14} className="mr-1"/>Delete</Button>
      </div>
    </div>
  );
}

export default function AdminReviews() {
  const { user, loading: authLoading } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [filter, setFilter] = useState("pending");
  const load = useCallback(() => api.get("/admin/reviews").then((r) => setReviews(r.data)).catch(() => toast.error("Could not load reviews")), []);
  useEffect(() => { if (user?.is_admin) load(); }, [user, load]);

  if (authLoading) return null;
  if (!user) return <Navigate to="/login" replace/>;
  if (!user.is_admin) return <Navigate to="/" replace/>;

  const setStatus = async (id, status) => {
    try { await api.post(`/admin/reviews/${id}/status`, { status }); toast.success(status === "approved" ? "Review posted to the site" : "Review hidden"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this review permanently?")) return;
    try { await api.delete(`/admin/reviews/${id}`); toast.success("Review deleted"); load(); } catch { toast.error("Could not delete"); }
  };
  const shown = filter === "all" ? reviews : reviews.filter((r) => r.status === filter);
  const count = (s) => reviews.filter((r) => r.status === s).length;

  return (
    <div data-testid="admin-reviews-page" className="max-w-6xl mx-auto px-4 sm:px-8 py-16">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <div className="uppercase text-[10px] tracking-[0.3em]" style={{ color: "var(--accent-metal)" }}>Admin · Reviews</div>
          <h1 className="font-serif text-4xl" style={{ color: "var(--primary)" }}>Customer Reviews</h1>
          <p className="text-sm text-[color:var(--text-muted)] mt-1">New reviews wait here. Only ones with posting permission can be approved.</p>
        </div>
        <div className="flex gap-2">
          <Link to="/admin/orders"><Button variant="outline" size="sm" className="rounded-full">Orders</Button></Link>
          <Link to="/admin/inbox"><Button variant="outline" size="sm" className="rounded-full">Inbox</Button></Link>
          <Button variant="outline" size="sm" className="rounded-full" onClick={load} data-testid="admin-reviews-refresh"><RefreshCw size={14} className="mr-1"/>Refresh</Button>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mb-6">
        {["pending", "approved", "hidden", "all"].map((s) => (
          <Button key={s} size="sm" data-testid={`admin-reviews-filter-${s}`} variant={filter === s ? "default" : "outline"} onClick={() => setFilter(s)} className={`rounded-full capitalize ${filter === s ? "btn-metal" : ""}`}>
            {s}{s !== "all" && ` (${count(s)})`}
          </Button>
        ))}
      </div>
      {shown.length === 0 ? (
        <div className="text-center py-16 rounded-lg border hairline bg-white text-sm text-[color:var(--text-muted)]" data-testid="admin-reviews-empty">No {filter === "all" ? "" : filter} reviews.</div>
      ) : (
        <div className="grid md:grid-cols-2 gap-4">{shown.map((r) => <AdminReviewCard key={r.review_id} r={r} onStatus={setStatus} onDelete={remove}/>)}</div>
      )}
    </div>
  );
}
